import * as React from 'react'
import * as z from 'zod'
import { max } from 'date-fns'

import { Heading } from '~/sotano-kit/primitives/heading'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { TabContentDefaultWrapper } from './wrapper'
import { BreadcrumbStickyContainer } from '../explore/components/tabs/dashboard-breadcrumb'
import { Breadcrumb, BreadcrumbItem } from '~/sotano-kit/primitives/breadcrumb'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/sotano-kit/primitives/tabs'
import { useBranches } from '~/hooks-data/use-branches'
import { useTimeDiff } from '~/hooks/use-time-diff'
import {
  Branch,
  Commit,
  branchBaseOps,
  isBranch,
} from '@basehub/data/replicache/index'
import { useCR } from '../branch/_hooks/use-merge-diff'
import { CopyTooltip } from '~/sotano-kit/primitives/copy-tooltip'
import { Pencil1Icon, ResetIcon } from '@radix-ui/react-icons'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useUserData } from '~/hooks-data/use-user-data'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserStore } from '~/app/_store/user-store'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '~/sotano-kit/primitives/dialog'
import { Label, LabeledInput } from '~/sotano-kit/primitives/input'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { getId } from '@basehub/utils/id/index'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { BranchIcon } from '~/components/icons/branch-icon'
import { toast } from 'sonner'
import {
  standardBranchSort,
  useSuggestedBranches,
} from '~/hooks-data/use-suggested-branches'
import { useCRMetadata } from '../branch/_hooks/use-cr-metadata'
import { parseTab, useTabsStore } from '../explore/components/tabs/store'
import { useRouter } from 'next/navigation'
import { BranchSwitcher } from '~/sotano-kit/primitives/branch-switcher'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { GitIcon } from '~/components/icons/git'
import { BranchBadge } from '../branch/_components/badge'
import { useToggleState } from '~/hooks/use-toggle-state'
import { ArchiveIcon } from '~/components/icons/archive'
import { getProviderBranchUrl } from '../branch/utils'
import { useMutation } from '@tanstack/react-query'
import { useIsExternalVisitor } from '~/hooks-data/use-user-role'
import { useCreateBranchMutator } from '~/replicache/frontend/use-create-branch-mutator'

export const BranchManager = () => {
  const addTab = useTabsStore((s) => s.addTab)
  const activeSubTabKey = useTabsStore((s) => {
    if (!s.activeTab) return 'active'
    const parsed = parseTab(s.activeTab)
    if (parsed.type !== 'branches') return 'active'
    return parsed.tab || 'active'
  })
  const isExternalVisitor = useIsExternalVisitor()

  return (
    <>
      <BreadcrumbStickyContainer>
        <Breadcrumb>
          <BreadcrumbItem active>Branches</BreadcrumbItem>
        </Breadcrumb>
      </BreadcrumbStickyContainer>
      <TabContentDefaultWrapper size="lg" fillerChildren>
        <div className="flex justify-between items-center mb-8">
          <Heading as="h2">Branches</Heading>

          <BranchCreateDialog>
            <Button>Create new branch</Button>
          </BranchCreateDialog>
        </div>

        <Tabs
          value={activeSubTabKey}
          onValueChange={(e) => {
            addTab({
              key: `branches:${e}`,
              partiallyOpen: false,
            })
          }}
        >
          <TabsList className="!p-0">
            <TabsTrigger
              value="active"
              className="group !text-dark-bright focus-visible:outline-none focus:outline-none focus-visible:ring-0 text-sm group relative cursor-default !min-w-0 radix-state-inactive:hover:!text-opacity-70 radix-state-inactive:!text-opacity-30 font-medium"
            >
              <div className="group-focus-visible:outline group-focus-visible:outline-dark-control-base group-focus-visible:outline-1 rounded-md absolute inset-1 group-active:outline-none" />
              Active
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="group !text-dark-bright focus-visible:outline-none focus:outline-none text-sm group relative cursor-default !min-w-0 radix-state-inactive:hover:!text-opacity-70 radix-state-inactive:!text-opacity-30 font-medium"
            >
              <div className="group-focus-visible:outline group-focus-visible:outline-dark-control-base group-focus-visible:outline-1 rounded-md absolute inset-1 group-active:outline-none" />
              Archived
            </TabsTrigger>
            <TabsTrigger
              value="mine"
              className="group !text-dark-bright focus-visible:outline-none focus:outline-none text-sm group relative cursor-default !min-w-0 radix-state-inactive:hover:!text-opacity-70 radix-state-inactive:!text-opacity-30 font-medium"
            >
              <div className="group-focus-visible:outline group-focus-visible:outline-dark-control-base group-focus-visible:outline-1 rounded-md absolute inset-1 group-active:outline-none" />
              Mine
            </TabsTrigger>
            {!isExternalVisitor && (
              <TabsTrigger
                value="suggested"
                className="group !text-dark-bright focus-visible:outline-none focus:outline-none text-sm group relative cursor-default !min-w-0 radix-state-inactive:hover:!text-opacity-70 radix-state-inactive:!text-opacity-30 font-medium"
              >
                <div className="group-focus-visible:outline group-focus-visible:outline-dark-control-base group-focus-visible:outline-1 rounded-md absolute inset-1 group-active:outline-none" />
                <SuggestedBranchesTriggerLabel />
              </TabsTrigger>
            )}
          </TabsList>

          <hr className="border-dark-border-solid -mt-px" />

          <TabsContent tabIndex={-1} value="mine" className="mt-8">
            <MyBranches />
          </TabsContent>
          <TabsContent tabIndex={-1} value="active" className="mt-8">
            <ActiveBranches />
          </TabsContent>
          <TabsContent tabIndex={-1} value="archived" className="mt-8">
            <ArchivedBranches />
          </TabsContent>
          {!isExternalVisitor && (
            <TabsContent tabIndex={-1} value="suggested" className="mt-8">
              <SuggestedBranches />
            </TabsContent>
          )}
        </Tabs>
      </TabContentDefaultWrapper>
    </>
  )
}

const SuggestedBranchesTriggerLabel = () => {
  const suggestedBranchesQuery = useSuggestedBranches()

  return (
    <span className="relative">
      Suggested from Git
      {Boolean(suggestedBranchesQuery.data?.length) && (
        <span className="absolute font-medium -top-2.5 -right-2.5 h-[14px] min-w-[14px] px-1 text-[11px] text-dark-bg bg-dark-accent rounded-full pointer-events-none select-none flex items-center justify-center">
          {suggestedBranchesQuery.data?.length}
        </span>
      )}
    </span>
  )
}

// Branch sections

const TableHeadRow = () => {
  return (
    <tr>
      <th>Branch name</th>
      <th>Source</th>
      <th>Linked Git branch</th>
      <th>Updated</th>
      <th className="!text-center">Behind | Ahead</th>
      {/* actions */}
      <th></th>
    </tr>
  )
}
const BranchRow = ({ branch }: { branch: Branch }) => {
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const timeDiff = useTimeDiff(
    max([
      new Date(
        branch.archivedAt ?? branch.headCommit?.createdAt ?? branch.createdAt
      ),
      new Date(branch.createdAt),
    ])
  )
  const user = useUserStore((s) => s.optimisticUser)
  const userId = user?.id
  const username = user?.username
  const { baseBranch } = useCR({ crId: branch.contentRequestIds?.[0] ?? null })
  const { data } = useRepoReplicache()
  const selectedBranchOrCommit = useSelectedBranchOrCommitQuery().data
  const linkedGitBranch = getProviderBranchUrl({ repo: data?.repo, branch })
  const isArchived = Boolean(branch.archivedAt)
  const isExternalVisitor = useIsExternalVisitor()
  const hasBranchRights = Boolean(
    !isExternalVisitor || branch.authorId === userId
  )
  const userQuery = useUserData(
    branch.headCommit?.authorId ?? baseBranch?.headCommit?.authorId
  )
  const lastCommitAuthor = userQuery.data
  const lastCommitAuthorFullName = lastCommitAuthor
    ? makeFullNameFromClerkUser(lastCommitAuthor)
    : ''
  const crStatus = useCRMetadata({ branch })

  return (
    <tr className="hover:bg-dark-shade">
      <td>
        <span className="flex items-center">
          <BranchBadge
            href={{
              pathname: `/${teamSlug}/${repoSlug}/branch/${encodeURIComponent(
                branch.name
              )}`,
            }}
            className="font-medium text-ellipsis overflow-hidden align-middle truncate !block"
            variant={
              isArchived || selectedBranchOrCommit?.id !== branch.id
                ? 'muted'
                : 'highlight'
            }
          >
            {branch.name}
          </BranchBadge>
          <CopyTooltip copyContent={branch.name} className="ml-2 size-[15px]" />
        </span>
      </td>

      <td>
        <span className="flex w-full">
          {branch.isDefault ? (
            <BranchBadge
              variant="muted"
              className="-ml-1 font-medium inline-block text-ellipsis overflow-hidden"
            >
              Default
            </BranchBadge>
          ) : (
            <span className="text-dark-dim text-ellipsis overflow-hidden">
              {baseBranch?.name}
            </span>
          )}
        </span>
      </td>
      <td>
        {linkedGitBranch ? (
          <ButtonLink
            className="flex items-center gap-x-1 group w-full overflow-hidden"
            href={linkedGitBranch}
            target="_blank"
            unstyled
            focusStyled
          >
            <GitIcon className="shrink-0" />
            <span className="group-hover:underline text-ellipsis overflow-hidden">
              {branch.git?.branch}
            </span>
          </ButtonLink>
        ) : (
          <span className="flex overflow-hidden">
            <span className="overflow-hidden text-ellipsis">
              {branch.git?.branch}
            </span>
          </span>
        )}
      </td>
      <td>
        {!isArchived && (
          <BaseHubAvatar
            id={lastCommitAuthor?.id ?? 'GHOST'}
            name={lastCommitAuthorFullName}
            withBorder
            className="inline align-middle w-[22px] h-[22px]"
          />
        )}
        <span className="align-middle ml-1">{`${
          isArchived ? 'Archived' : ''
        } ${timeDiff}`}</span>
      </td>
      <td className="text-center">
        <BranchBadge
          variant="muted"
          className="-ml-1 inline-flex items-center gap-x-0.5 align-middle"
        >
          {crStatus.data?.behind ?? 0}
          <ChangesBehindIcon />
        </BranchBadge>
        &nbsp;&nbsp;
        <BranchBadge
          variant="muted"
          className="-ml-1 inline-flex items-center gap-x-0.5 align-middle"
        >
          <ChangesBehindIcon className="rotate-180" />
          {crStatus.data?.ahead ?? 0}
        </BranchBadge>
      </td>
      <td>
        {hasBranchRights && (
          <IconButton
            disabled={
              branch.isDefault || branch.id === selectedBranchOrCommit?.id
            }
            onClick={async (e) => {
              e.preventDefault()
              if (!data?.replicache) return
              if (branch.isDefault) return
              if (!userId || !username) {
                return toast.error(
                  'Could not archive branch, this is a bug in BaseHub.'
                )
              }

              await data.replicache.mutate.toggleArchiveBranch({
                id: branch.id,
                archivedAt: new Date().toISOString(),
                archivedBy: userId,
                archivedByUsername: username,
              })
            }}
          >
            {isArchived ? (
              <ResetIcon width={16} height={16} />
            ) : (
              <ArchiveIcon width={16} height={16} />
            )}
          </IconButton>
        )}
        {Boolean(!isArchived && hasBranchRights) && (
          <BranchEditDialog branch={branch}>
            <IconButton>
              <Pencil1Icon width={16} height={16} />
            </IconButton>
          </BranchEditDialog>
        )}
      </td>
    </tr>
  )
}

const MyBranches = () => {
  const { optimisticUser } = useUserStore()
  const branchesQuery = useBranches({
    authorId: optimisticUser?.id,
    archived: false,
  })
  const sortedBranches = [...(branchesQuery.data ?? [])].sort(
    standardBranchSort
  )

  return (
    <>
      <Heading as="h4" size="xs" className="mb-3">
        My branches
      </Heading>

      <div className="basehub-table-wrapper">
        <table className="[&_td]:h-12 [&_th]:h-12 text-dark-muted table-fixed">
          <thead className="bg-dark-shade text-sm">
            <TableHeadRow />
          </thead>

          <tbody className="text-xs bg-dark-base">
            {sortedBranches.length ? (
              sortedBranches.map((branch) => {
                return <BranchRow key={branch.id} branch={branch} />
              })
            ) : (
              <BranchRowEmpty>No branches authored by you</BranchRowEmpty>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

export const ChangesBehindIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="15"
      fill="none"
    >
      <path
        fill="currentColor"
        d="M11.594 7.5a.469.469 0 0 1-.469.469H3.351l3.419 3.418a.47.47 0 0 1-.664.663L1.887 7.832a.469.469 0 0 1 0-.664l4.22-4.218a.469.469 0 1 1 .663.663L3.35 7.03h7.775a.469.469 0 0 1 .47.469ZM13.137 2.012a.469.469 0 0 1 .8.332v10.312a.469.469 0 1 1-.937 0V2.344c0-.125.05-.244.137-.332Z"
      />
    </svg>
  )
}

const ActiveBranches = () => {
  const branchesQuery = useBranches({ status: 'open', archived: false })
  const sortedBranches = [...(branchesQuery.data ?? [])].sort(
    standardBranchSort
  )

  return (
    <>
      <Heading as="h4" size="xs" className="mb-3">
        Active branches
      </Heading>

      <div className="basehub-table-wrapper">
        <table className="[&_td]:h-12 [&_th]:h-12 text-dark-muted table-fixed">
          <thead className="bg-dark-shade text-sm">
            <TableHeadRow />
          </thead>

          <tbody className="text-xs bg-dark-base">
            {sortedBranches.length ? (
              sortedBranches.map((branch) => (
                <BranchRow key={branch.id} branch={branch} />
              ))
            ) : (
              <BranchRowEmpty>No active branches</BranchRowEmpty>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

const ArchivedBranches = () => {
  const branchesQuery = useBranches({ archived: true })
  const sortedBranches = [...(branchesQuery.data ?? [])].sort(
    standardBranchSort
  )

  return (
    <>
      <Heading as="h4" size="xs" className="mb-3">
        Archived branches
      </Heading>

      <div className="basehub-table-wrapper">
        <table className="[&_td]:h-12 [&_th]:h-12 text-dark-muted table-fixed">
          <thead className="bg-dark-shade text-sm">
            <TableHeadRow />
          </thead>

          <tbody className="text-xs bg-dark-base">
            {sortedBranches.length ? (
              sortedBranches.map((branch) => (
                <BranchRow key={branch.id} branch={branch} />
              ))
            ) : (
              <BranchRowEmpty>No archived branches</BranchRowEmpty>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

const SuggestedBranches = () => {
  const suggestedBranches = useSuggestedBranches()
  const { data } = useRepoReplicache()

  return (
    <>
      <Heading as="h4" size="xs" className="mb-1">
        Suggested branches
      </Heading>
      <p className="mb-4 text-dark-muted text-sm leading-normal text-pretty max-w-2xl">
        Branches that come from your Git repository, which we suggest you link
        to a BaseHub branch. API traffic that comes from a linked Git branch
        will be redirected to the respective BaseHub branch.
      </p>

      <div className="basehub-table-wrapper">
        <table className="[&_td]:h-12 [&_th]:h-12 text-dark-muted table-fixed">
          <thead className="bg-dark-shade text-sm">
            <tr>
              <th>Git branch</th>
              {/* actions */}
              <th></th>
            </tr>
          </thead>

          <tbody className="text-xs">
            {suggestedBranches.data?.length ? (
              suggestedBranches.data.map((suggestedBranch) => {
                const gitBranchUrl =
                  data?.repo.gitRepoUrl && suggestedBranch.git?.branch
                    ? `${data.repo.gitRepoUrl}/tree/${suggestedBranch.git?.branch}`
                    : ''

                return (
                  <tr key={suggestedBranch.id}>
                    <td>
                      {gitBranchUrl ? (
                        <ButtonLink
                          unstyled
                          focusStyled
                          href={gitBranchUrl}
                          target="_blank"
                          className="flex items-center max-w-max gap-x-1 group w-full overflow-hidden"
                        >
                          <GitIcon className="shrink-0" />
                          <span className="group-hover:underline text-ellipsis overflow-hidden">
                            {suggestedBranch.git?.branch}
                          </span>
                        </ButtonLink>
                      ) : (
                        <span className="flex overflow-hidden">
                          <span className="overflow-hidden text-ellipsis">
                            {suggestedBranch.git?.branch}
                          </span>
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        intent="secondary"
                        className="mr-2"
                        size="small"
                        aria-label="Dismiss suggested branch"
                        onClick={async () => {
                          if (!data?.replicache) return

                          await data.replicache.mutate.resolveSuggestedBranch({
                            id: suggestedBranch.id,
                            resolution: { type: 'dismissed' },
                            resolvedAt: new Date().toISOString(),
                          })
                        }}
                      >
                        Dismiss
                      </Button>
                      <BranchLinkDialog
                        git={{ branch: suggestedBranch.git?.branch ?? '' }}
                      >
                        <Button size="small">Link</Button>
                      </BranchLinkDialog>
                    </td>
                  </tr>
                )
              })
            ) : (
              <BranchRowEmpty>No suggested branches</BranchRowEmpty>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

const BranchRowEmpty = ({ children }: { children: React.ReactNode }) => {
  return (
    <tr>
      <td className="!text-left">{children}</td>
      <td />
      <td />
      <td />
      <td />
      <td />
    </tr>
  )
}

const BranchEditDialog = ({
  children,
  branch,
}: {
  children: React.ReactNode
  branch: Branch
}) => {
  const { data } = useRepoReplicache()
  const router = useRouter()
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { mutate: handleSubmit, isLoading } = useMutation(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!data?.replicache) return
      const formData = new FormData(e.target as HTMLFormElement)
      const branchName = z.string().parse(formData.get('branch-name'))
      const gitBranch = z.string().parse(formData.get('git-branch'))

      await data.replicache.mutate
        .updateBranch({
          id: branch.id,
          isoNow: new Date().toISOString(),
          data: {
            name: branchName,
            git: { branch: gitBranch },
          },
        })
        .then(() => {
          router.push(
            `/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
              branchName
            )}`
          )
        })
    }
  )

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent asChild>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Edit {branch.name}</DialogTitle>
          <div>
            <LabeledInput
              label="Branch name"
              name="branch-name"
              className="px-6 py-1.5 last:pb-4 first:pt-4 -mx-6"
              size="xs"
              blocked={false}
              inputProps={{
                defaultValue: branch.name,
                size: 'xs',
                required: true,
                max: 250,
              }}
            />
            <LabeledInput
              label="Linked Git branch"
              name="git-branch"
              className="px-6 py-1.5 last:pb-4 first:pt-4 -mx-6"
              size="xs"
              blocked={false}
              inputProps={{ defaultValue: branch.git?.branch, size: 'xs' }}
            />
          </div>

          <DialogFooter divider>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button>{isLoading ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const BranchCreateDialog = ({
  children,
  branchData,
}: {
  children: React.ReactNode
  branchData?: {
    name: string
    git: { branch: string }
    isSuggestedBranch?: boolean
  }
}) => {
  const { data } = useRepoReplicache()
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const branches = useBranches({ status: 'open', archived: false })
  const sortedBranches = [...(branches.data ?? [])].sort(standardBranchSort)
  const { optimisticUser } = useUserStore()
  const selectedBranchOrCommitQuery = useSelectedBranchOrCommitQuery()
  const branchOrCommit = selectedBranchOrCommitQuery.data
  const createBranch = useCreateBranchMutator()

  if (!branchOrCommit) return null

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!data?.replicache || !optimisticUser || !branchOrCommit) return
    const formData = new FormData(e.target as HTMLFormElement)
    const branchName = z.string().parse(formData.get('branch-name'))
    const gitBranch = z.string().parse(formData.get('git-branch'))
    const sourceBranchId = z.string().parse(formData.get('source-branch'))

    const sourceBranch = await data.replicache.query((tx) =>
      branchBaseOps.get(tx, sourceBranchId)
    )

    if (!sourceBranch) {
      return toast.error('Source branch not found')
    }

    await createBranch({
      checkout: true,
      toastSuccess: true,
      toastError: true,
      baseBranchOrCommitId: sourceBranch.id,
      idSeed: getId(),
      data: {
        name: branchName,
        id: getId(),
        isDefault: false,
        createdAt: new Date().toISOString(),
        git: { branch: gitBranch },
        authorId: optimisticUser.id,
      },
    })
  }

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent asChild>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Create new branch</DialogTitle>

          <div>
            <LabeledInput
              label="Branch name"
              name="branch-name"
              className="px-6 py-1.5 last:pb-4 first:pt-4 -mx-6"
              size="xs"
              blocked={false}
              inputProps={{
                defaultValue: branchData?.name ?? '',
                size: 'xs',
                required: true,
                max: 250,
              }}
            />
            <Label
              label="Source"
              size="xs"
              className="px-6 py-1.5 -mx-6"
              blocked={false}
            >
              <Select
                name="source-branch"
                defaultValue={
                  isBranch(branchOrCommit)
                    ? branchOrCommit.id
                    : (sortedBranches[0]?.id ?? '')
                }
              >
                <SelectTrigger className="font-medium">
                  <SelectValue placeholder="Select base branch" />
                  <BranchIcon className="mr-0.5" width={15} height={15} />
                </SelectTrigger>
                <SelectContent className="font-medium">
                  {sortedBranches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Label>
            <LabeledInput
              label="Linked Git branch"
              name="git-branch"
              className="px-6 py-1.5 last:pb-4 first:pt-4 -mx-6"
              size="xs"
              blocked={false}
              inputProps={{
                size: 'xs',
                disabled: !!branchData?.isSuggestedBranch,
                defaultValue:
                  branchData?.git.branch ??
                  (isBranch(branchOrCommit) ? branchOrCommit.git?.branch : ''),
              }}
            />
          </div>

          <DialogFooter divider>
            <DialogClose asChild ref={cancelRef}>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button>Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const BranchLinkDialog = ({
  children,
  git,
}: {
  children: React.ReactNode
  git: { branch: string }
}) => {
  const router = useRouter()
  const { data } = useRepoReplicache()
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const { optimisticUser } = useUserStore()
  const selectedBranchOrCommitQuery = useSelectedBranchOrCommitQuery()
  const branchOrCommit = selectedBranchOrCommitQuery.data
  const [newBranchToCreate, setNewBranchToCreate] =
    React.useState<Branch | null>(null)
  const branchesQuery = useBranches({ status: 'open' })
  const branches = branchesQuery.data ?? []
  const sortedBranches = [...branches].sort(standardBranchSort)
  const activeBranches = sortedBranches.filter((b) => !b.archivedAt)
  const createBranch = useCreateBranchMutator()
  const confirmButtonRef = React.useRef<HTMLButtonElement>(null)
  const toggleState = useToggleState()
  const [selectedBranch, setSelectedBranch] = React.useState<
    Branch | Commit | null
  >(selectedBranchOrCommitQuery.data ?? null)

  React.useEffect(() => {
    // Find a link between existing branch name and suggested branch git link
    setSelectedBranch((selectedBranch) => {
      if (selectedBranch) return selectedBranch
      let defaultBranch = null

      return (
        activeBranches.find((b) => {
          if (b.name === git.branch) return true
          if (b.isDefault) defaultBranch = b
        }) ??
        defaultBranch ??
        activeBranches[0] ??
        null
      )
    })
  }, [activeBranches, git.branch])

  if (!branchOrCommit) return null

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!data?.replicache || !optimisticUser || !branchOrCommit) {
      return
    }

    if (newBranchToCreate) {
      const branchName = newBranchToCreate.name

      await createBranch({
        checkout: true,
        toastSuccess: true,
        toastError: true,
        baseBranchOrCommitId: branchOrCommit.id,
        idSeed: getId(),
        data: {
          authorId: optimisticUser?.id,
          name: branchName,
          id: getId(),
          isDefault: false,
          createdAt: new Date().toISOString(),
          git: { branch: git.branch },
        },
      })
    } else {
      await data.replicache.mutate
        .updateBranch({
          id: selectedBranch!.id,
          data: { git: { branch: git.branch } },
          isoNow: new Date().toISOString(),
        })
        .then(({ name }) => {
          if (!newBranchToCreate) {
            if (selectedBranch?.id !== selectedBranchOrCommitQuery.data?.id) {
              toast.success('Branch linked. Checking it out...')
            } else {
              toast.success('Branch linked.')
              toggleState.handleToggle()
            }
          } else {
            toast.success('Branch created. Checking it out...')
          }
          router.push(
            `/${data.team.slug}/${data.repo.slug}/explore/${encodeURIComponent(
              name
            )}`
          )
        })
        .catch((err) => {
          if (err instanceof MutatorUserError) toast.error(err.reason)
          else toast.error('An error occurred while linking the branch')
        })
    }
  }

  const resolvedBranch = newBranchToCreate ?? selectedBranch!

  return (
    <Dialog open={toggleState.isOn} onOpenChange={toggleState.handleToggle}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent asChild>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Link Git branch</DialogTitle>
          <DialogDescription>
            API traffic from the Git Branch will be routed to the linked BaseHub
            Branch.
          </DialogDescription>

          <div className="flex gap-x-1.5 text-sm text-dark-muted justify-between mt-7">
            <span className="flex-2 whitespace-nowrap text-ellipsis overflow-hidden">
              <GitIcon className="inline-block mr-1.5" width={15} height={15} />
              <span className="underline">{git.branch}</span>
            </span>
            <span className="flex-1 whitespace-nowrap">will be linked to</span>
            <div className="flex-1 flex justify-end">
              {selectedBranch && (
                <BranchSwitcher
                  popoverProps={{
                    align: 'end',
                    onCloseAutoFocus: (e) => {
                      e.preventDefault()
                    },
                  }}
                  hideBranchManager
                  activeBranchOrCommit={resolvedBranch}
                  branches={
                    newBranchToCreate
                      ? [...activeBranches, newBranchToCreate]
                      : activeBranches
                  }
                  header={false}
                  onBranchCreate={async ({ name }) => {
                    setNewBranchToCreate({
                      createdAt: new Date().toISOString(),
                      isDefault: false,
                      name,
                      id: getId(),
                      headCommitId: null,
                      workingRootBlockId: null,
                      git: { branch: git.branch },
                    })
                    setSelectedBranch(null)
                    setTimeout(() => confirmButtonRef.current?.focus(), 100)
                  }}
                  onSelect={(b) => {
                    setTimeout(() => confirmButtonRef.current?.focus(), 100)
                    if (b.id === resolvedBranch?.id) return
                    setNewBranchToCreate(null)
                    setSelectedBranch(b)
                  }}
                />
              )}
            </div>
          </div>

          <DialogFooter divider>
            <DialogClose asChild ref={cancelRef}>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button ref={confirmButtonRef}>
              {newBranchToCreate ? 'Create and Link' : 'Link'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
